import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Mail, Phone, MapPin, Calendar, Upload, Send, Globe } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import SEOHead from './SEOHead';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: '',
    ndaAccepted: false
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  
  // Animation refs
  const [headerRef, headerVisible] = useScrollAnimation();
  const [formRef, formVisible] = useScrollAnimation(200);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'application/zip')) {
      setSelectedFile(file);
    } else {
      alert('Please select a PDF or ZIP file only.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          message: formData.message
        })
      });

      const result = await response.json();

      if (response.ok && result.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          message: '',
          ndaAccepted: false
        });
        setSelectedFile(null);
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCalendly = () => {
    // Mock Calendly integration
    window.open(companyInfo.calendlyUrl, '_blank');
  };

  return (
    <section id="contact" className="py-20 lg:py-32 bg-gradient-to-b from-[#f0f0e8]/20 to-white">
      <SEOHead 
        title="Contact MedQuore - Medical Device Quality Consulting | Get Started"
        description="Contact MedQuore for expert medical device quality and regulatory consulting. ISO 13485, EU MDR, FDA compliance support. Schedule a consultation today."
        keywords="contact medquore, medical device consulting, quality consulting contact, ISO 13485 consultation, EU MDR consulting, medical device experts"
        canonical="https://medquore.com/contact"
        ogTitle="Contact MedQuore - Medical Device Quality Experts"
        ogDescription="Ready to achieve audit readiness? Contact our medical device quality and compliance experts for personalized consulting services."
      />
      <div className="container mx-auto px-4 lg:px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-white rounded-full px-4 py-2 mb-6 shadow-sm">
            <Mail className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">Get In Touch</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            Ready to Achieve Audit Readiness?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Let's discuss how MedQuore can accelerate your compliance journey and reduce time-to-market. 
            Schedule a consultation or send us your requirements.
          </p>
        </div>

        <div className="grid lg:grid-cols-1 gap-12 max-w-4xl mx-auto">
          {/* Contact Form - Now full width */}
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-6">
              <h3 className="text-2xl font-bold text-[#011431]">Send us a Message</h3>
              <p className="text-gray-600">Send your message directly to {companyInfo.email} - we'll respond within 24 hours with a tailored approach for your needs.</p>
            </CardHeader>
            <CardContent>
              {/* Success/Error Messages */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">✓ Thank you for your message!</p>
                  <p className="text-green-700 text-sm mt-1">
                    Your inquiry has been sent to {companyInfo.email} and we will respond within 24 hours.
                  </p>
                </div>
              )}
              
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">✗ Error sending message</p>
                  <p className="text-red-700 text-sm mt-1">
                    Please try again or contact us directly at {companyInfo.email}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-[#c41927] focus:ring-[#c41927]"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company *
                    </label>
                    <Input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-[#c41927] focus:ring-[#c41927]"
                      placeholder="Company name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-[#c41927] focus:ring-[#c41927]"
                      placeholder="your.email@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="border-gray-300 focus:border-[#c41927] focus:ring-[#c41927]"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="border-gray-300 focus:border-[#c41927] focus:ring-[#c41927]"
                    placeholder="Tell us about your project, timeline, and specific needs..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Attach Documents (PDF/ZIP)
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="file"
                      id="file-upload"
                      accept=".pdf,.zip"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg px-4 py-2 cursor-pointer transition-colors"
                    >
                      <Upload className="w-4 h-4 text-gray-600" />
                      <span className="text-sm text-gray-700">Choose File</span>
                    </label>
                    {selectedFile && (
                      <span className="text-sm text-gray-600">{selectedFile.name}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="nda"
                    checked={formData.ndaAccepted}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, ndaAccepted: checked }))
                    }
                    className="mt-1"
                  />
                  <label htmlFor="nda" className="text-sm text-gray-600 leading-relaxed">
                    I agree to MedQuore's standard NDA for confidential project discussions. 
                    All information shared will be treated with strict confidentiality.
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !formData.ndaAccepted}
                  className="w-full bg-[#c41927] hover:bg-[#a01520] text-white font-semibold py-3 group"
                >
                  {isSubmitting ? (
                    "Sending..."
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Contact;